import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChartBarIcon, FlagIcon, SparklesIcon } from '@heroicons/react/24/outline';
import {
  formatCo2e,
  fmtTPerT,
  unitColor,
  normUnit,
  BOP_BENCHMARK_EXCLUDED,
  fmtMonthKey,
  buildBopData,
  moduleColor,
} from '@/utils/gconUtils';

const QUARTILE_META = [
  { label: 'Q1', tag: 'Best', color: '#02AD74' },
  { label: 'Q2', tag: null, color: '#00A99D' },
  { label: 'Q3', tag: null, color: '#FF8F12' },
  { label: 'Q4', tag: 'Worst', color: '#DC2626' },
];

const TIP_W = 132;

const BAR_H = 80; // px of bar track; values and the target line share this scale
const BAR_HEAD = 18; // room above the tallest bar for its value label

const QuartileBars = ({ row, target }) => {
  const [tip, setTip] = useState(null); // { qi, x, y } in viewport coords
  // Include the target in the scale so its line is always on the chart
  const max = Math.max(...row.qAvgs.filter(v => v != null), target?.value ?? 0, 0.000001);
  const px = v => (v / max) * BAR_H;

  const track = (qi, e) => setTip({ qi, x: e.clientX, y: e.clientY });
  const months = tip ? row.quartiles[tip.qi] : null;

  return (
    <div>
      <div className="relative flex items-end gap-2" style={{ height: BAR_H + BAR_HEAD }}>
        {row.qAvgs.map((v, qi) => {
          const meta = QUARTILE_META[qi];
          const h = v != null ? Math.max(px(v), 2) : 2;
          const isBest = qi === 0;
          const isHot = tip?.qi === qi;
          return (
            <div
              key={meta.label}
              onMouseEnter={e => track(qi, e)}
              onMouseMove={e => track(qi, e)}
              onMouseLeave={() => setTip(null)}
              className="relative flex-1 h-full cursor-default"
            >
              <span
                className={`absolute inset-x-0 text-center text-xs font-semibold ${
                  isBest ? 'text-green-600' : 'text-gray-600'
                }`}
                style={{ bottom: h + 2 }}
              >
                {v != null ? fmtTPerT(v) : '—'}
              </span>
              <div
                className="absolute inset-x-0 bottom-0 rounded-t transition-all"
                style={{
                  height: h,
                  backgroundColor: meta.color,
                  opacity: isHot || isBest ? 1 : 0.55,
                  outline: isHot ? `2px solid ${meta.color}` : 'none',
                  outlineOffset: 1,
                }}
              />
            </div>
          );
        })}

        {target && (
          <div
            className="absolute inset-x-0 border-t-2 border-dashed border-gray-800 pointer-events-none"
            style={{ bottom: px(target.value) }}
            title={`${target.year} target: ${fmtTPerT(target.value)} t CO₂e/t`}
          >
            <span className="absolute right-0 bottom-0.5 text-[10px] font-semibold text-gray-800 bg-white/85 px-1 rounded">
              {target.year} target {fmtTPerT(target.value)}
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-1">
        {QUARTILE_META.map((meta, qi) => (
          <span
            key={meta.label}
            className={`flex-1 text-center text-xs ${qi === 0 ? 'font-semibold text-green-700' : 'text-gray-400'}`}
          >
            {meta.label}
          </span>
        ))}
      </div>

      {/* Cursor-following tooltip. Fixed positioning escapes the card and the
          scroll container, so it never clips; nudged back inside near an edge. */}
      {months?.length > 0 && (
        <div
          className="fixed z-50 pointer-events-none bg-gray-900 text-white rounded shadow-xl px-2 py-1.5 text-[11px] leading-snug"
          style={{
            width: TIP_W,
            left: Math.min(tip.x + 14, window.innerWidth - TIP_W - 8),
            top: Math.min(tip.y + 14, window.innerHeight - (30 + months.length * 15) - 8),
          }}
        >
          <div className="flex items-center gap-1.5 pb-1 mb-1 border-b border-white/15">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: QUARTILE_META[tip.qi].color }}
            />
            <span className="font-semibold">{QUARTILE_META[tip.qi].label}</span>
            <span className="ml-auto text-gray-400 tabular-nums">{fmtTPerT(row.qAvgs[tip.qi])}</span>
          </div>
          {months.map(o => (
            <div key={o.key} className="flex items-center justify-between gap-2">
              <span className="text-gray-400">{fmtMonthKey(o.key)}</span>
              <span className="tabular-nums">{fmtTPerT(o.intensity)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BopCard = ({ row, highlight, colorOf = unitColor, target }) => {
  // Positive = still above (worse than) the target
  const vsTarget = target ? ((row.actual - target.value) / target.value) * 100 : null;
  return (
    <div className={`bg-white border rounded-lg p-4 ${highlight ? 'border-green-200' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {!highlight && (
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorOf(row.label) }} />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{row.label}</p>
            <p className="text-xs text-gray-400">
              {row.obs.length} months · {row.quartiles[0].length} in best quartile
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-gray-400">B.O.P. benchmark</p>
          <p className="text-sm font-semibold text-green-600">{fmtTPerT(row.best)} t CO₂e/t</p>
        </div>
      </div>

      <QuartileBars row={row} target={target} />

      <div className={`grid ${target ? 'grid-cols-4' : 'grid-cols-3'} gap-2 mt-3 pt-3 border-t border-gray-100`}>
        {[
          { label: 'Actual avg', value: `${fmtTPerT(row.actual)} t/t` },
          { label: 'Gap to best', value: `${row.gapPct.toFixed(1)}%`, warn: row.gapPct > 15 },
          { label: 'Savings at B.O.P.', value: formatCo2e(row.savings), good: row.savings > 0 },
          ...(target
            ? [
                vsTarget > 0
                  ? { label: `vs ${target.year} target`, value: `${vsTarget.toFixed(1)}% above`, warn: true }
                  : { label: `vs ${target.year} target`, value: 'Meets target', good: true },
              ]
            : []),
        ].map(s => (
          <div key={s.label}>
            <p className="text-xs text-gray-400">{s.label}</p>
            <p
              className={`text-xs font-semibold ${
                s.warn ? 'text-orange-600' : s.good ? 'text-green-600' : 'text-gray-700'
              }`}
            >
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {row.bestMonths.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          <span className="text-xs text-gray-400">Best months:</span>
          {row.bestMonths.map(k => (
            <span key={k} className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium">
              {fmtMonthKey(k)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const DEFAULT_TARGET_YEAR = 2030;
// Shown first in the editor: the categories targets are usually set for
const PRIORITY_CATEGORIES = ['stationary combustion', 'flare'];

const toInput = v => (v == null ? '' : String(v));
const fromInput = v => (v.trim() === '' ? null : Number(v));

const TargetEditor = ({ targets, categoryRows, plant, onSave, onClose }) => {
  const [year, setYear] = useState(String(targets.year ?? DEFAULT_TARGET_YEAR));
  const [plantValue, setPlantValue] = useState(toInput(targets.plant));
  const [byCategory, setByCategory] = useState(
    Object.fromEntries(Object.entries(targets.by_category ?? {}).map(([k, v]) => [k, toInput(v)])),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const rank = label => {
    const i = PRIORITY_CATEGORIES.indexOf(label.toLowerCase());
    return i < 0 ? PRIORITY_CATEGORIES.length : i;
  };
  const categories = [...categoryRows].sort((a, b) => rank(a.label) - rank(b.label));

  const values = [plantValue, ...Object.values(byCategory)].map(fromInput);
  const invalid = values.some(v => v != null && (!Number.isFinite(v) || v < 0)) || !/^\d{4}$/.test(year.trim());

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        year: Number(year),
        plant: fromInput(plantValue),
        by_category: Object.fromEntries(
          Object.entries(byCategory)
            .map(([k, v]) => [k, fromInput(v)])
            .filter(([, v]) => v != null),
        ),
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data ? 'Check the values and try again.' : 'Could not save targets.');
      setSaving(false);
    }
  };

  const field = (label, value, onChange, hint) => (
    <label key={label} className="flex items-center gap-3 py-1.5">
      <span className="flex-1 min-w-0">
        <span className="block text-xs font-medium text-gray-700 truncate">{label}</span>
        {hint && <span className="block text-[11px] text-gray-400">{hint}</span>}
      </span>
      <input
        type="number"
        min="0"
        step="any"
        inputMode="decimal"
        placeholder="No target"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input input-xs input-bordered w-28 text-right tabular-nums"
      />
    </label>
  );
  const hintFor = row => (row ? `Actual ${fmtTPerT(row.actual)} · Q1 ${fmtTPerT(row.best)} t/t` : undefined);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <p className="text-sm font-semibold text-gray-800">Emissions intensity targets</p>
        <label className="flex items-center gap-2 text-xs text-gray-500">
          Target year
          <input
            value={year}
            onChange={e => setYear(e.target.value)}
            inputMode="numeric"
            maxLength={4}
            className="input input-xs input-bordered w-16 text-center tabular-nums"
          />
        </label>
      </div>
      <p className="text-xs text-gray-400 mb-2">
        In t CO₂e per t of product. Targets are drawn as a dashed line on the B.O.P. charts; leave a field blank for no
        target.
      </p>

      <div className="divide-y divide-gray-100">
        {field('Overall plant', plantValue, setPlantValue, hintFor(plant))}
        {categories.map(row =>
          field(
            row.label,
            byCategory[row.label] ?? '',
            v => setByCategory(p => ({ ...p, [row.label]: v })),
            hintFor(row),
          ),
        )}
      </div>

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={invalid || saving}>
          {saving ? <span className="loading loading-spinner loading-xs" /> : 'Save targets'}
        </button>
      </div>
    </div>
  );
};

const BopView = ({ unitData, moduleData, targets = {}, onSaveTargets }) => {
  const [editingTargets, setEditingTargets] = useState(false);
  const [mode, setMode] = useState('unit'); // unit | module
  const hasModules = (moduleData?.units ?? []).length > 0;
  const byModule = mode === 'module' && hasModules;
  const data = byModule ? moduleData : unitData;
  const noun = byModule ? 'category' : 'unit';

  const { rows, plant } = buildBopData(data);
  // Category targets live on the category cards; unit cards have none of their own
  const categoryBop = buildBopData(moduleData);
  const targetYear = targets.year ?? DEFAULT_TARGET_YEAR;
  const targetFor = value => (value == null ? null : { value, year: targetYear });
  const plantTarget = targetFor(targets.plant);
  const rowTarget = row => (byModule ? targetFor(targets.by_category?.[row.label]) : null);
  const hasTargets = targets.plant != null || Object.keys(targets.by_category ?? {}).length > 0;

  const toggle = hasModules && (
    <div className="join flex-shrink-0">
      {[
        { key: 'unit', label: 'By Unit' },
        { key: 'module', label: 'By Category' },
      ].map(opt => (
        <button
          key={opt.key}
          type="button"
          className={`join-item btn btn-xs ${mode === opt.key ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMode(opt.key)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  // Months where any group has both emissions and production
  const usableMonths = (data?.months ?? []).filter((_, i) =>
    (data?.units ?? []).some(u => (u.values?.[i] ?? 0) > 0 && (u.production?.[i] ?? 0) > 0),
  ).length;

  if (!rows.length && !plant) {
    return (
      <div className="p-6 h-full flex flex-col gap-4">
        {toggle && <div className="flex justify-end">{toggle}</div>}
        <div className="flex flex-col items-center justify-center flex-1 text-gray-400 gap-2 px-8 text-center">
          <ChartBarIcon className="w-10 h-10" />
          <p className="text-sm">Not enough months to build quartiles.</p>
          <p className="text-xs">
            B.O.P. needs at least 4 months with both emissions and production for a {noun}; this selection has{' '}
            {usableMonths} {usableMonths === 1 ? 'month' : 'months'}. Widen the month range or add more data.
          </p>
        </div>
      </div>
    );
  }

  const totalSavings = rows.reduce((s, r) => s + r.savings, 0);
  const totalActual = rows.reduce((s, r) => s + r.totalCo2e, 0);
  // DPCU 2 runs on a different intensity basis than the gas processing units, so
  // it can't win the cross-unit benchmark. It still gets its own card and still
  // counts toward the total opportunity — only the "best performer" pick skips it.
  // Categories share a plant-wide denominator, so the exclusion doesn't apply.
  const benchmarkRows = byModule ? rows : rows.filter(r => !BOP_BENCHMARK_EXCLUDED.has(normUnit(r.label)));
  const bestUnit = [...benchmarkRows].sort((a, b) => a.best - b.best)[0];
  const excludedFromBenchmark = byModule ? [] : rows.filter(r => BOP_BENCHMARK_EXCLUDED.has(normUnit(r.label)));
  const biggestGap = [...rows].sort((a, b) => b.gapPct - a.gapPct)[0];

  return (
    <div className="p-6 overflow-y-auto h-full space-y-5">
      {/* Explainer */}
      <div className="bg-white border border-gray-100 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-4 h-4 text-green-500" />
            <p className="text-sm font-semibold text-gray-800">Best Operating Plant (B.O.P.)</p>
          </div>
          <div className="flex items-center gap-2">
            {onSaveTargets && (
              <button
                type="button"
                className={`btn btn-xs gap-1 ${editingTargets ? 'btn-ghost' : 'btn-outline'}`}
                onClick={() => setEditingTargets(v => !v)}
              >
                <FlagIcon className="w-3.5 h-3.5" />
                {hasTargets ? `${targetYear} targets` : 'Set targets'}
              </button>
            )}
            {toggle}
          </div>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">
          Each {noun}&apos;s monthly emission intensity (CO₂e ÷ production) is ranked and split into quartiles. The{' '}
          <span className="text-green-600 font-medium">best quartile (Q1)</span> — the lowest-intensity months — becomes
          the benchmark: it is performance the plant has already demonstrated. &ldquo;Savings at B.O.P.&rdquo; is what
          the period would have emitted had every month matched that Q1 average.
          {byModule && ' Categories span multiple units, so intensity is measured against plant-wide production.'}
        </p>
      </div>

      {editingTargets && (
        <TargetEditor
          targets={targets}
          categoryRows={categoryBop.rows}
          plant={categoryBop.plant ?? plant}
          onSave={onSaveTargets}
          onClose={() => setEditingTargets(false)}
        />
      )}

      {hasTargets && !byModule && Object.keys(targets.by_category ?? {}).length > 0 && (
        <p className="text-xs text-gray-500 -mt-2">
          Category targets are shown under <strong>By Category</strong>; the plant-wide target is shown on both views.
        </p>
      )}

      {/* KPI strip */}
      <div className="flex gap-3">
        {[
          {
            label: 'Total B.O.P. opportunity',
            value: formatCo2e(totalSavings),
            sub: totalActual > 0 ? `${((totalSavings / totalActual) * 100).toFixed(1)}% of ${noun} emissions` : '—',
          },
          {
            label: byModule ? 'Lowest intensity category' : 'Best performing unit',
            value: bestUnit?.label ?? '—',
            sub: bestUnit
              ? `${fmtTPerT(bestUnit.best)} t CO₂e/t at Q1${
                  excludedFromBenchmark.length ? ` · excl. ${excludedFromBenchmark.map(r => r.label).join(', ')}` : ''
                }`
              : '—',
          },
          {
            label: 'Largest gap to best',
            value: biggestGap?.label ?? '—',
            sub: biggestGap ? `${biggestGap.gapPct.toFixed(1)}% above its own Q1` : '—',
          },
        ].map(s => (
          <div key={s.label} className="flex-1 bg-white border border-gray-100 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
            <p className="text-sm font-semibold text-gray-800 truncate">{s.value}</p>
            <p className="text-xs text-gray-400">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Plant-wide */}
      {plant && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {byModule ? 'All categories combined' : 'Plant-wide'}
          </p>
          <BopCard row={plant} highlight target={plantTarget} />
        </div>
      )}

      {/* Per unit / category */}
      {rows.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            By {noun} — ranked by savings opportunity
          </p>
          <div className="grid grid-cols-2 gap-3">
            {rows.map(r => (
              <BopCard key={r.label} row={r} colorOf={byModule ? moduleColor : unitColor} target={rowTarget(r)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const targetShape = PropTypes.shape({
  value: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
});

QuartileBars.propTypes = { row: PropTypes.object.isRequired, target: targetShape };

BopCard.propTypes = {
  row: PropTypes.object.isRequired,
  highlight: PropTypes.bool,
  colorOf: PropTypes.func,
  target: targetShape,
};

const targetsShape = PropTypes.shape({
  year: PropTypes.number,
  plant: PropTypes.number,
  by_category: PropTypes.objectOf(PropTypes.number),
});

TargetEditor.propTypes = {
  targets: targetsShape.isRequired,
  categoryRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  plant: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BopView.propTypes = {
  unitData: PropTypes.object,
  moduleData: PropTypes.object,
  targets: targetsShape,
  onSaveTargets: PropTypes.func,
};

export default BopView;
